import React from 'react';
import Link from 'next/link';

interface FleetStatsProps {
  stats: {
    total_devices: number;
    active_devices: number;
    healthy_devices: number;
    warning_devices: number;
    critical_devices: number;
    offline_devices?: number;
    auto_discovered_devices: number;
    devices_needing_review: number;
    avg_health_score: number;
  };
}

export const FleetStats: React.FC<FleetStatsProps> = ({ stats }) => {
  const statCards = [
    {
      title: 'Total Systems',
      value: stats.total_devices,
      subtitle: `${stats.active_devices} active`,
      color: 'blue',
      icon: '🏭',
      href: null
    },
    {
      title: 'Healthy Systems',
      value: stats.healthy_devices,
      subtitle: `${stats.total_devices > 0 ? ((stats.healthy_devices / stats.total_devices) * 100).toFixed(0) : 0}% of fleet`,
      color: 'green',
      icon: '✅',
      href: null
    },
    {
      title: 'Need Attention',
      value: stats.warning_devices + stats.critical_devices,
      subtitle: `${stats.warning_devices} warning, ${stats.critical_devices} critical`,
      color: stats.critical_devices > 0 ? 'red' : 'yellow',
      icon: '⚠️',
      href: '/attention'
    },
    {
      title: 'Offline',
      value: stats.offline_devices || 0,
      subtitle: 'Not responding',
      color: 'purple',
      icon: '🔌',
      href: null
    },
    {
      title: 'Auto-Discovered',
      value: stats.auto_discovered_devices,
      subtitle: `${stats.devices_needing_review} pending review`,
      color: stats.devices_needing_review > 0 ? 'orange' : 'purple',
      icon: '🔍',
      href: '/device-review'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
      {statCards.map((stat, index) => {
        const cardContent = (
          <div className={`rounded-lg border-2 p-6 ${getColorClasses(stat.color)} ${stat.href ? 'hover:shadow-lg transition-shadow cursor-pointer' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">{stat.icon}</span>
              <span className="text-3xl font-bold">{stat.value}</span>
            </div>
            <h3 className="font-semibold text-lg mb-1">{stat.title}</h3>
            <p className="text-sm opacity-80">{stat.subtitle}</p>
            {stat.href && (
              <div className="mt-2 text-xs opacity-60">Click to view details →</div>
            )}
          </div>
        );

        return stat.href ? (
          <Link key={index} href={stat.href}>
            {cardContent}
          </Link>
        ) : (
          <div key={index}>
            {cardContent}
          </div>
        );
      })}
      
      {/* Average Health Score */}
      <div className="lg:col-span-5 bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg text-gray-800 mb-2">Fleet Health Score</h3>
            <div className="flex items-center space-x-4">
              <span className="text-3xl font-bold text-gray-900">
                {stats.avg_health_score?.toFixed(1) || '--'}%
              </span>
              <div className="flex-1 max-w-md">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-300 ${
                      stats.avg_health_score >= 80 ? 'bg-green-500' :
                      stats.avg_health_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(stats.avg_health_score || 0, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-right text-sm text-gray-600">
            <p>Target: ≥80%</p>
            <p>
              Status: {
                stats.avg_health_score >= 80 ? '🟢 Excellent' :
                stats.avg_health_score >= 60 ? '🟡 Good' : '🔴 Needs Attention'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};